/**
 * Modal "Etiquetas GS1 DataMatrix" — grilla de etiquetas para imprimir.
 *
 * Cada etiqueta incluye:
 *  - GS1 DataMatrix (canvas vía bwip-js) con AIs (01)(17)(10)(21)
 *  - Producto + presentación
 *  - GTIN + Lote + Vencimiento + N° Serie legibles
 *
 * Acciones:
 *  - Imprimir vía `window.print()` con CSS `@media print` que oculta todo
 *    excepto la grilla.
 *  - Descargar PDF A4 4×6 (24 etiquetas/página) vía jsPDF tomando los canvas
 *    de bwip-js como PNG (ver `generarPdfEtiquetas`).
 *
 * Performance: si el lote tiene >200 series, se pagina la vista en chunks
 * de 200 para no congelar el browser. PDF abarca el total siempre.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '@/lib/notify';
import { Loader2, Printer, FileDown, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { construirStringGs1 } from '@/domain/gs1';
import { generarPdfEtiquetas } from '@/features/pdf/etiquetas-pdf';
import { fmtNum } from '@/lib/format';
import type { Serie } from '@/api/contracts/series';
import type { Producto } from '@/api/contracts/productos';

const ETIQUETAS_POR_PAGINA = 200;

export interface EtiquetasGs1ModalProps {
  open: boolean;
  onClose: () => void;
  /** Producto (para nombre + presentación en la etiqueta). */
  producto: Producto | null;
  /** Series a etiquetar — todas comparten GTIN+Lote+Vto. */
  series: Serie[];
  /** Texto opcional arriba del título (ej. "Lote ING-001 · 100 series"). */
  contexto?: string;
}

export function EtiquetasGs1Modal({
  open,
  onClose,
  producto,
  series,
  contexto,
}: EtiquetasGs1ModalProps) {
  const [pagina, setPagina] = useState(0);
  const [generandoPdf, setGenerandoPdf] = useState(false);

  // Paginar para no rendear 10.000 canvases de golpe
  const totalPaginas = Math.max(1, Math.ceil(series.length / ETIQUETAS_POR_PAGINA));
  const seriesPagina = useMemo(() => {
    const start = pagina * ETIQUETAS_POR_PAGINA;
    return series.slice(start, start + ETIQUETAS_POR_PAGINA);
  }, [series, pagina]);

  // Reset paginación al abrir
  useEffect(() => {
    if (open) setPagina(0);
  }, [open]);

  const handlePrint = () => {
    // CSS .print-area está en globals.css — al imprimir solo se muestra eso
    window.print();
  };

  const handleDownloadPdf = () => {
    if (!producto) return;
    setGenerandoPdf(true);
    generarPdfEtiquetas({
      producto,
      series,
      nombreArchivo: `etiquetas-${producto.gtin}-${series[0]?.lote ?? 'lote'}.pdf`,
    })
      .then(() => toast.success(`PDF generado · ${series.length} etiquetas`))
      .catch((err: Error) => toast.error(`Error generando PDF: ${err.message}`))
      .finally(() => setGenerandoPdf(false));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto">
        <DialogHeader className="no-print">
          <DialogTitle>Etiquetas GS1 DataMatrix</DialogTitle>
          <DialogDescription>
            {contexto && <div className="mb-1 text-xs text-everex-azul">{contexto}</div>}
            {producto?.nombre} · {fmtNum(series.length)} serie
            {series.length !== 1 ? 's' : ''}.{' '}
            Imprimí con <kbd className="rounded bg-gray-100 px-1">Cmd+P</kbd> /{' '}
            <kbd className="rounded bg-gray-100 px-1">Ctrl+P</kbd> o descargá el PDF.
          </DialogDescription>
        </DialogHeader>

        {totalPaginas > 1 && (
          <div className="no-print flex items-center justify-between rounded border border-gray-200 bg-gray-50 p-2 text-xs">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setPagina((p) => Math.max(0, p - 1))}
              disabled={pagina === 0}
            >
              <ChevronLeft className="h-3 w-3" /> Prev
            </Button>
            <div className="text-gray-600">
              Página <b>{pagina + 1}</b> de {totalPaginas} ·{' '}
              <span className="text-gray-600">
                etiquetas {pagina * ETIQUETAS_POR_PAGINA + 1}–
                {Math.min((pagina + 1) * ETIQUETAS_POR_PAGINA, series.length)}
              </span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setPagina((p) => Math.min(totalPaginas - 1, p + 1))}
              disabled={pagina >= totalPaginas - 1}
            >
              Sig <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        )}

        <div className="print-area grid grid-cols-2 gap-2 md:grid-cols-4">
          {producto &&
            seriesPagina.map((s) => (
              <EtiquetaIndividual
                key={s.id}
                gtin={s.gtin}
                lote={s.lote}
                vencimiento={s.vencimiento}
                serieId={s.id}
                productoNombre={producto.nombre}
                productoPresentacion={producto.presentacion}
              />
            ))}
        </div>

        <DialogFooter className="no-print">
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
          <Button
            variant="outline"
            onClick={handlePrint}
            className="border-everex-azul text-everex-azul hover:bg-everex-azul-bg"
          >
            <Printer className="mr-1 h-4 w-4" /> Imprimir
          </Button>
          <Button
            onClick={handleDownloadPdf}
            disabled={generandoPdf || series.length === 0}
            className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
          >
            {generandoPdf ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <FileDown className="mr-1 h-4 w-4" />
            )}
            Descargar PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface EtiquetaIndividualProps {
  gtin: string;
  lote: string;
  vencimiento: string;
  serieId: string;
  productoNombre: string;
  productoPresentacion: string;
}

function EtiquetaIndividual({
  gtin,
  lote,
  vencimiento,
  serieId,
  productoNombre,
  productoPresentacion,
}: EtiquetaIndividualProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const stringGs1 = construirStringGs1({ gtin, lote, vencimiento, serieId });
    let cancelado = false;
    // Lazy-load bwip-js — la SPA principal no debe arrastrar 450KB de barcode
    // si el usuario nunca abre el modal de etiquetas.
    void import('bwip-js/browser').then(({ default: bwipjs }) => {
      if (cancelado || !canvasRef.current) return;
      try {
        bwipjs.toCanvas(canvasRef.current, {
          bcid: 'gs1datamatrix',
          text: stringGs1,
          scale: 3,
          paddingwidth: 5,
          paddingheight: 5,
          parsefnc: true,
        });
      } catch (err) {
        // Renderizar un canvas con error visible para no romper la grilla
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          ctx.fillStyle = '#fee2e2';
          ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          ctx.fillStyle = '#991b1b';
          ctx.font = '12px monospace';
          ctx.fillText('ERROR GS1', 10, 30);
          ctx.fillText((err as Error).message.slice(0, 25), 10, 50);
        }
      }
    });
    return () => {
      cancelado = true;
    };
  }, [gtin, lote, vencimiento, serieId]);

  return (
    <div className="break-inside-avoid rounded border border-gray-300 bg-white p-2 text-center">
      <div className="text-[11px] font-bold text-everex-azul">{productoNombre}</div>
      <div className="text-[11px] text-gray-600">{productoPresentacion}</div>
      <canvas ref={canvasRef} className="mx-auto my-1" />
      <div className="space-y-0.5 text-left font-mono text-[11px] text-gray-700">
        <div>
          <span className="text-gray-600">GTIN:</span> {gtin}
        </div>
        <div>
          <span className="text-gray-600">Lote:</span> {lote}
        </div>
        <div>
          <span className="text-gray-600">Vto:</span> {vencimiento}
        </div>
        <div className="font-bold">
          <span className="text-gray-600">S/N:</span> {serieId}
        </div>
      </div>
    </div>
  );
}
